import React, { useCallback, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useExperimentGatedAppTakeover } from 'features/app-takeover/hooks/use-app-takeover';
import { useMaybePlayAudioOnceOnMount } from 'features/app-takeover/utils/play-audio-once';
import { MenuReorderWidget } from 'features/menu/components/menu-reorder-widget';
import { MenuSection } from 'features/menu/components/menu-section/menu-section';
import { MenuSkeletonLoader } from 'features/menu/components/menu-tile-grid/menu-tile-grid-loading';
import withMenuEmptyState from 'features/menu/components/with-menu-empty-state';
import { useMenuScroll } from 'features/menu/hooks/use-menu-scroll';
import { MenuEmptyState } from 'features/menu/pages/main-menu-page/menu-empty-state';
import { useStoreContext } from 'features/store';
import { View } from 'react-native';

import { Helmet } from 'components/helmet';
import { LayoutContainer } from 'components/layout/styled';
import { usePullToReload } from 'components/pull-to-reload/pull-to-reload';
import { asPreviewMenuEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/as-preview-menu-entry';
import { useOneMenuCatalog } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-one-menu-catalog';
import { useUnavailableItemId } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-unavailable-item-id';
import { useUnavailableSectionId } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-unavailable-section-id';
import { buildUnavailableProductFullMenuRecoveryData } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/build-unavailable-product-full-menu-recovery-data';
import {
  buildCatalogSectionNodes,
  buildUnavailableSectionRecoveryData,
} from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/build-unavailable-section-recovery-data';
import { resolveUnavailableSectionNode } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/resolve-unavailable-section-node';
import { useDeeplinksFlowEnabled } from 'experiments/deeplinks-flow-feature/use-deeplinks-flow-experiment';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useNativeTabBarContentInset } from 'hooks/use-native-tab-bar-content-inset';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { useMenuContext } from 'state/menu';
import { useServiceModeLive } from 'state/service-mode';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { routes } from 'utils/routing';
import { waitForDuration } from 'utils/wait-for-duration';

type OtherAvailableHeader = 'options' | 'categories';

// NEW one menu
const MainMenuPage = () => {
  useMenuScroll();
  const router = useRouter();

  const {
    mainMenu,
    indexedMenu,
    isLoading: isMenuLoading,
    refetchMenu,
    rawMenu,
    hasError,
    loadedStoreNumber,
  } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const { activeDayParts, showBurgersForBreakfast } = useMenuContext();
  const serviceMode = useServiceModeLive();
  const { language } = useLocale();
  const isDeeplinksFlowEnabled = useDeeplinksFlowEnabled();
  const unavailableSectionIdParam = useUnavailableSectionId();
  const unavailableItemIdParam = useUnavailableItemId();
  const treatmentUnavailableSectionId = isDeeplinksFlowEnabled
    ? unavailableSectionIdParam
    : undefined;
  const treatmentUnavailableItemId = isDeeplinksFlowEnabled ? unavailableItemIdParam : undefined;

  const needsCatalog = Boolean(treatmentUnavailableSectionId || treatmentUnavailableItemId);
  const { entries, rootMenuEntryId, findEntry } = useOneMenuCatalog({ skip: !needsCatalog });

  const isMobile = useIsMobileBreakpoint();
  const { store } = useStoreContext();
  const isCurrentStoreMenuLoaded = Boolean(
    store?.number && loadedStoreNumber === store.number && rawMenu
  );

  const isLoading = isMenuLoading || (!hasError && !isCurrentStoreMenuLoaded);

  const { menuData, deeplinkUnavailableItemId, otherAvailableHeader } = useMemo((): {
    menuData: typeof mainMenu;
    deeplinkUnavailableItemId?: string;
    otherAvailableHeader: OtherAvailableHeader;
  } => {
    // Product/combo/picker whose parent section is missing — pin the product
    // Not Available card above other available options (store categories).
    if (treatmentUnavailableItemId) {
      const resolveEntry = (entryId: string) => {
        const storeEntry = indexedMenu.find(entryId);
        if (storeEntry) {
          return storeEntry;
        }
        const catalogEntry = findEntry(entryId) ?? entries.find(entry => entry.id === entryId);
        return catalogEntry ? asPreviewMenuEntry(catalogEntry, language) : undefined;
      };

      const productRecovery = buildUnavailableProductFullMenuRecoveryData({
        unavailableItemId: treatmentUnavailableItemId,
        storeSections: mainMenu ?? [],
        resolveEntry,
        posServiceMode,
      });

      if (productRecovery) {
        return {
          menuData: productRecovery.data,
          deeplinkUnavailableItemId: productRecovery.deeplinkUnavailableItemId,
          otherAvailableHeader: 'options',
        };
      }
    }

    if (!treatmentUnavailableSectionId) {
      return {
        menuData: mainMenu,
        deeplinkUnavailableItemId: undefined,
        otherAvailableHeader: 'options',
      };
    }

    const localized = entries.map(entry => asPreviewMenuEntry(entry, language));
    const catalogSections = buildCatalogSectionNodes(localized, rootMenuEntryId);
    const unavailableNode = resolveUnavailableSectionNode({
      unavailableSectionId: treatmentUnavailableSectionId,
      catalogSections,
      catalogEntries: localized,
      storeSections: mainMenu ?? [],
    });

    if (!unavailableNode) {
      return {
        menuData: mainMenu,
        deeplinkUnavailableItemId: undefined,
        otherAvailableHeader: 'options',
      };
    }

    const storeOthers = (mainMenu ?? []).filter(
      section => section.id !== treatmentUnavailableSectionId
    );
    const sectionsForRecovery =
      storeOthers.length > 0
        ? [unavailableNode, ...storeOthers]
        : catalogSections.length > 0
        ? catalogSections
        : [unavailableNode];

    const recovery = buildUnavailableSectionRecoveryData(
      sectionsForRecovery,
      treatmentUnavailableSectionId
    );

    if (!recovery) {
      return {
        menuData: mainMenu,
        deeplinkUnavailableItemId: undefined,
        otherAvailableHeader: 'options',
      };
    }

    return {
      menuData: recovery.data,
      deeplinkUnavailableItemId: recovery.deeplinkUnavailableSectionId,
      otherAvailableHeader: 'categories',
    };
  }, [
    entries,
    findEntry,
    indexedMenu,
    language,
    mainMenu,
    posServiceMode,
    rootMenuEntryId,
    treatmentUnavailableItemId,
    treatmentUnavailableSectionId,
  ]);

  const handleUnavailableChooseRestaurant = useCallback(() => {
    if (!deeplinkUnavailableItemId) {
      return;
    }
    const entry =
      indexedMenu.find(deeplinkUnavailableItemId) ??
      (() => {
        const catalogEntry =
          findEntry(deeplinkUnavailableItemId) ??
          entries.find(e => e.id === deeplinkUnavailableItemId);
        return catalogEntry ? asPreviewMenuEntry(catalogEntry, language) : undefined;
      })();
    const typeSegment = String(entry?.type ?? 'item').toLowerCase();
    router.navigate({
      pathname: routes.minimalStoreLocator,
      params: {
        redirectUrl: `${routes.menu}/${typeSegment}/${deeplinkUnavailableItemId}`,
        unavailableItemId: deeplinkUnavailableItemId,
      },
    });
  }, [deeplinkUnavailableItemId, entries, findEntry, indexedMenu, language, router]);

  const onRefresh = async () => {
    // This is just an animation feedback of one second
    await waitForDuration(1000);
    refetchMenu();
  };

  const pullToReloadProps = usePullToReload({
    onRefresh,
    isDisabled: isLoading,
  });
  const nativeTabBarContentInset = useNativeTabBarContentInset();

  useMaybePlayAudioOnceOnMount(useExperimentGatedAppTakeover()?.menu?.audio, {
    shouldPlay: !isLoading && mainMenu?.length > 0,
    globalPlayOnce: true,
  });

  if (isLoading) {
    return <MenuSkeletonLoader />;
  }

  return (
    <LayoutContainer isFullContainer={isMobile}>
      <Helmet title={t('mainMenu')} />
      <View style={{ flex: 1 }}>
        <MenuReorderWidget />
        {menuData.length > 0 ? (
          <MenuSection
            data={menuData}
            paddingBottom={nativeTabBarContentInset}
            deeplinkUnavailableItemId={deeplinkUnavailableItemId}
            otherAvailableHeader={otherAvailableHeader}
            onUnavailableChooseRestaurant={
              treatmentUnavailableItemId && deeplinkUnavailableItemId
                ? handleUnavailableChooseRestaurant
                : undefined
            }
            {...pullToReloadProps}
          />
        ) : (
          <MenuEmptyState
            onRefresh={onRefresh}
            logWhenTrue={!isLoading}
            logContext={{
              menuType: '1m',
              activeDayParts: activeDayParts.map(dayPart => dayPart.key),
              showBurgersForBreakfast,
              serviceMode,
              rawMenuLength: rawMenu?.entries?.length,
              isFetchedMenuEmpty: !isCurrentStoreMenuLoaded || (rawMenu?.entries.length ?? 0) === 0,
              isCurrentStoreMenuLoaded,
              isPricingAndDependenciesSettled: true,
              storeId: store?.number,
              storePosVendor: store?.pos?.vendor,
            }}
          />
        )}
      </View>
    </LayoutContainer>
  );
};

// MainMenuPage.whyDidYouRender = true;
export const MainMenuPageV2 = withMenuEmptyState(MainMenuPage);
